import { can } from "../../permissions/"
import systemFields from "../systemFields.js"

const initializePolicy = `
mutation initializePolicy($policy: initializePolicyInput){
  initializePolicy(policy: $policy){
    status
    errors
    policyStatus{
      id
    }
  }
}`

const issuePolicy = `
mutation issuePolicy($callApi:  Boolean!, $policyId: String!){
  issuePolicy(callApi: $callApi, policyId: $policyId){
    status
    errors
    policyStatus{
      id
      status
    }
  }
}
`

const addBeneficiaryEligibility = `
mutation addBeneficiaryEligibility($policyId:  String!, $beneficiaryEligibilityInput: beneficiaryEligibilityInput!){
  addBeneficiaryEligibility(policyId: $policyId, beneficiaryEligibilityInput: $beneficiaryEligibilityInput){
    status
    errors
  }
}
`

const placeTravelOrder = `
mutation placeTravelOrder($tenantId: String!, $policy: createPolicyInput!) {
  createPolicy(tenantId: $tenantId, policy: $policy) {
    errors
    status
    policyStatus{
      id
      status
    }
  }
}
`

const readAll = () => `
query policies($skip: Int, $limit: Int, $sort: sortInput, $where: policyWhereInput){
  policies(skip: $skip, limit: $limit, sort: $sort, where: $where){
    totalCount
    list{
      id
      source
      ${systemFields()}
      issuerNumber
      pricing{
        amount
        currencyCode
        appliedDiscounts{
          code
          ratio
          order
          originalPrice
        }
      }

      holder{
        contacts{ id type value }
      }

      #commissions
      commissionSummary {
        currencyCode
        amount
        entity{
          name
        }
        ratio
      }

      ${
				can("readProducts")
					? `product{ name version type insurer{ name } productId{type plan version} }`
					: ""
			}
      status
      startDate
      endDate
      issueDate
      ${can("readInternals") ? "referrer{ name }" : ""}
      ${
				can("readIndividuals") || can("readCompanies")
					? `holder{
          ${
						can("readIndividuals")
							? "... on individual { __typename id name }"
							: ""
					}
          ${
						can("readCompanies") ? "... on company { __typename id name }" : ""
					} 
        }`
					: ""
			} 
    }
  }
}
`

const readOne = () => `
query policies($tenantId: String! $where: policyWhereInput $skip: Int, $limit: Int, $sort: sortInput,){
  policies(where:$where skip: $skip, limit: $limit, sort: $sort,){
    list{
      ${systemFields()}
      id
      source
      issuerNumber
      cancellationReason
      pricing{
        amount
        currencyCode
        appliedDiscounts{
          code
          ratio
          order
          originalPrice
        }
      }
      commissionSplitRules {
        id
        baseCommissionRatio{
          ratio
        }
        currencyCode
        amount
        ratio
        type
        entity{
          name
        }
      }
      commissionSummary {
        currencyCode
        amount
        entity{
          name
        }
      }
      paymentInfos{
        id
        method
        name
        amount
        frequency
      }
      status
      startDate
      endDate
      issueDate
      description
      beneficiaryEligibilities{
        id
        entity{
          id
          name
        }
        notes
      }
      contractBeneficiaryEligibilities{
        id
        entity{
          id
          name
        }
        notes
      }
      insured{ 
        id
        ... on individual { __typename id name }
        ... on company { __typename id name } 
        ... on object { __typename id name }
      }
      attachments{ path fileName ${systemFields()} }
      notes{ id title content ${systemFields()} }
      product{
        productId{
          plan
          type
          version
        }
        name 
        version
        type
        insurer{ id name logoUrls{ typeA } }
        benefits(typeIds: [], includeOptionals: true) { typeId value }
      } 
      referrer{ name }
      holder{ 
        id
        ... on individual { 
            __typename id name 
            contacts{ id type value }
            facts{
              id
              type
              value{
                booleanValue
                dateValue
                numberValue
                stringValue
                arrayValue {
                  stringValue
                }
              } 
            }
        }
        ... on company { 
            __typename id name 
            contacts{ id type value }
            facts{
              id
              type 
              value{
                booleanValue
                dateValue
                numberValue
                stringValue
                arrayValue {
                  stringValue
                }
              } 
            }
        }
      } 

      contractHolder{  
        ... on individual {
          __typename
          id
          name
          englishFirstName
          englishLastName
          chineseFirstName
          chineseLastName
          dateOfBirth
          gender
          occupation
          maritalStatus
          employmentStatus
          salutation
          preferredCommunicationChannel
          preferredLanguage
          countryOfResidency
          contacts{ id type value }
          identities{ id type value }
          addresses{ id fields type }
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        ... on company {
          __typename
          id
          name
          registrationNumber
          natureOfBusiness
          contacts{ id type value }
          addresses{ id fields type }
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
      }
    
      contractInsured{
        ... on individual {
          __typename
          id
          name
          englishFirstName
          englishLastName
          chineseFirstName
          chineseLastName
          dateOfBirth
          gender
          occupation
          maritalStatus
          employmentStatus
          salutation
          preferredCommunicationChannel
          preferredLanguage
          countryOfResidency
          contacts{ id type value }
          identities{ id type value }
          addresses{ id fields type }
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        ... on company {
          __typename
          id
          name
          contacts{ id type value }
          addresses{ id fields type }
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        ... on object {
          __typename
          id
          name
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        
      }
      otherContractHolders{
        ... on individual {
          __typename
          id
          name
          englishFirstName
          englishLastName
          chineseFirstName
          chineseLastName
          dateOfBirth
          gender
          occupation
          maritalStatus
          employmentStatus
          salutation
          preferredCommunicationChannel
          preferredLanguage
          countryOfResidency
          contacts{ id type value }
          identities{ id type value }
          addresses{ id fields type }
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        ... on company {
          __typename
          id
          name
          contacts{ id type value }
          addresses{ id fields type }
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        ... on object {
          __typename
          id
          name
          facts{
            id
            type 
            value{
              booleanValue
              dateValue
              numberValue
              stringValue
              arrayValue {
                stringValue
              }
            } 
          }
        }
        
      }
    }
  }
  benefitInfos(tenantId: $tenantId){ name typeId }
}
`

const update = `
mutation update($policyId: String! $updateInput: updatePolicyInput!){
  updatePolicy(policyId: $policyId updatedPolicy: $updateInput){
    errors
    status
  }
}
`

const addCommissionRuleToPolicy = `mutation addCommissionRuleToPolicy($policyId:String! $input:commissionRuleInputGraph!){
  addCommissionRuleToPolicy(policyId:$policyId input:$input){
    status
    errors
  }
}
`

const removeCommissionRuleFromPolicy = `mutation removeCommissionRuleFromPolicy($policyId:String! $id:String!){
  removeCommissionRuleFromPolicy(policyId:$policyId id:$id){
    status
    errors
  }
}
`

const updatePolicyCommissionRule = `mutation updatePolicyCommissionRule($policyId:String! $id:String! $input:commissionRuleInputGraph!){
  updatePolicyCommissionRule(policyId:$policyId id:$id input:$input){
    status
    errors
  }
}
`

const commissionSplitAndSummary = () => `
query commissionSplitAndSummary($where:policyWhereInput) {
  policies(where:$where){
    list {
      commissionSplitRules {
        id
        entity {
          __typename
          id
          name
        }
        baseCommissionRatio {
          ratio
        }
        currencyCode
        amount
        ratio
        type
        entity {
          name
        }
        jsonRule
      }
      commissionSummary {
        currencyCode
        amount
        ratio
        entity{
          __typename
          id
          name
        }
      }
    }
  }
}
`

const commissionSummary = () => `
query commissionSummary($where:policyWhereInput) {
  policies(where:$where){
    list {
      commissionSummary {
        currencyCode
        amount
        entity{
          __typename
          id
          name
        }
      }
    }
  }
}
`

const updateContractInsuredPerson = `
mutation updateContractInsured($policyId: String! $entityId: String! $input: updateInsuredInput!){
  updateContractInsured(policyId: $policyId entityId: $entityId input: $input){
    errors
    status
  }
}
`

const deleteBeneficiaryEligibility = `
mutation removeBeneficiaryEligibility($policyId: String! $beneficiaryEligibilityId: String!){
  removeBeneficiaryEligibility(policyId: $policyId, beneficiaryEligibilityId: $beneficiaryEligibilityId){
    errors
    status
  }
}
`

const deleteFile = `
mutation removePolicyAttachment($policyId: String! $fileName: String!){
  removePolicyAttachment(policyId: $policyId fileName: $fileName){
    errors
    status
  }
}
`

const addNote = `
mutation addPolicyNote($policyId: String! $noteInput: addNoteInput!){
  addPolicyNote(policyId: $policyId noteInput: $noteInput){
    errors
    status
  }
}
`

const updateNote = `
mutation updateNote($policyId: String! $updateNoteInput: updateNoteInput!){
  updatePolicyNote(policyId: $policyId updateNoteInput: $updateNoteInput){
    errors
    status
  }
}
`

const deleteNote = `
mutation removePolicyNote($policyId: String! $id: String!){
  removePolicyNote(policyId: $policyId id: $id){
    errors
    status
  }
}
`

const cancelPolicy = `
mutation cancelPolicy($policyId: String! $reason: String!){
  cancelPolicy(policyId: $policyId reason: $reason){
    status
    errors
  }
}
`

const remove = `
mutation deletePolicy($id: String!){
  deletePolicy(id: $id){
    errors
    status
  }
}
`

const numberOfPolicies = () => `
query policies($where: policyWhereInput){
  policies(where: $where){
    totalCount
  }
}
`

const sendCancellationEmail = `
mutation sendCancellationEmail($input: sendNotificationInput!){
  sendNotification(input: $input){
    status
    errors
  }
}
`

const query = {
	readAll,
	readOne,
	numberOfPolicies,
	commissionSummary,
	commissionSplitAndSummary
}

const mutation = {
	initializePolicy,
	issuePolicy,
	addBeneficiaryEligibility,
	addCommissionRuleToPolicy,
	removeCommissionRuleFromPolicy,
	updatePolicyCommissionRule,
	placeTravelOrder,
	update,
	updateContractInsuredPerson,
	addNote,
	deleteNote,
	updateNote,
	deleteFile,
	deleteBeneficiaryEligibility,
	remove,
	cancelPolicy,
	sendCancellationEmail
}

export { query, mutation }
